/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { typography2 } from '@coursera/cds-core';
import { useVisibilityTracker } from '@coursera/event-pulse/react';

import TrackedDiv from 'bundles/page/components/TrackedDiv';
import type { AutocompleteMode } from 'bundles/search-common/constants/autocomplete';
import { type AutocompleteItems, getAutocompleteEventingData } from 'bundles/search-common/utils/autocomplete';

const AutocompleteOptionTrackingDiv = ({
  children,
  autocompleteState,
  id,
  recentlyViewedItems,
  suggestionIndex,
  searchText,
}: {
  children: React.ReactNode;
  autocompleteState: AutocompleteMode;
  id: string;
  recentlyViewedItems: AutocompleteItems;
  suggestionIndex?: number;
  searchText?: string;
}) => {
  const trackingData = getAutocompleteEventingData(id, autocompleteState, recentlyViewedItems, suggestionIndex);
  const trackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_autocomplete_suggestions',
    trackingData,
    [],
    { fullyVisible: true, initialDelay: 1000 }
  );

  // Bolden the matching part of the text if searchText is provided
  const renderChildren = React.useMemo(() => {
    const isString = typeof children === 'string';

    if (!searchText || !isString) {
      return isString ? (
        <span
          css={css`
            ${typography2.subtitleMedium}
          `}
        >
          {children}
        </span>
      ) : (
        children
      );
    }

    const lowerChildren = children.toLowerCase();
    const lowerSearchText = searchText.toLowerCase();
    const matchIndex = lowerChildren.indexOf(lowerSearchText);

    if (matchIndex === -1) {
      return (
        <span
          css={css`
            ${typography2.subtitleMedium}
          `}
        >
          {children}
        </span>
      );
    }

    const beforeMatch = children.substring(0, matchIndex);
    const match = children.substring(matchIndex, matchIndex + searchText.length);
    const afterMatch = children.substring(matchIndex + searchText.length);

    return (
      <span
        css={css`
          ${typography2.subtitleMedium}
        `}
      >
        {beforeMatch}
        <strong>{match}</strong>
        {afterMatch}
      </span>
    );
  }, [children, searchText]);

  return (
    <div ref={trackingRef}>
      <TrackedDiv withVisibilityTracking requireFullyVisible trackingName="autocomplete_item">
        {renderChildren}
      </TrackedDiv>
    </div>
  );
};
export default AutocompleteOptionTrackingDiv;
